const chapter = {
  id: 'living-world',
  slug: 'living-world',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'The Living World',
        hindi: 'जीव जगत',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1-2 questions in NEET',
        hindi: 'NEET में 1-2 प्रश्न',
  },
  syllabusTags: ['Diversity in Living World', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'characteristics',
      title: {
        english: 'What is "Living"?',
        hindi: '"लिविंग" क्या है?',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Defining features (in order of importance)**:',
        hindi: '**विशेषताओं को परिभाषित करना (महत्व के क्रम में)**:',
            },
            {
              english: '• **Metabolism** — sum of all chemical reactions in body. **Defining feature** of living organisms (no non-living thing exhibits metabolism).',
        hindi: '• **चयापचय** — शरीर में सभी रासायनिक प्रतिक्रियाओं का योग। **जीवित जीवों की परिभाषित विशेषता** (कोई भी निर्जीव वस्तु चयापचय प्रदर्शित नहीं करती)।',
            },
            {
              english: '• **Cellular organisation** — body made of cells.',
        hindi: '• **कोशिकीय संगठन** — कोशिकाओं से बना शरीर।',
            },
            {
              english: '• **Consciousness** — ability to sense surroundings/internal environment & respond. **Most obvious + complex defining property**.',
        hindi: '• **चेतना** — surroundings/internal वातावरण को समझने और प्रतिक्रिया देने की क्षमता। **सबसे स्पष्ट + जटिल परिभाषित संपत्ति**।',
            },
            {
              english: '• **Growth, reproduction, response to stimuli** — important but **not defining** (mules/sterile workers cannot reproduce; crystals grow).',
        hindi: '• **विकास, प्रजनन, उत्तेजनाओं पर प्रतिक्रिया** - महत्वपूर्ण लेकिन **परिभाषित नहीं** (mules/sterile श्रमिक पुनरुत्पादन नहीं कर सकते; क्रिस्टल बढ़ते हैं)।',
            },
            {
              english: 'In unicellular organisms, **growth = reproduction** (binary fission).',
        hindi: 'एककोशिकीय जीवों में, **वृद्धि = प्रजनन** (बाइनरी विखंडन)।',
            },
            {
              english: 'Growth in non-living = accumulation on surface; in living = **internal (intussusception)**.',
        hindi: 'निर्जीव में वृद्धि = सतह पर संचय; जीवित रहने में = **आंतरिक (अंतर्ग्रहण)**।',
            },
          ],
        },
      ],
    },
    {
      id: 'diversity',
      title: {
        english: 'Diversity in Living World',
        hindi: 'जीव जगत में विविधता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Biodiversity** — number & types of organisms on Earth.',
        hindi: '**जैव विविधता**—पृथ्वी पर जीवों की संख्या और प्रकार।',
            },
            {
              english: 'Number of described species ≈ **1.7-1.8 million**.',
        hindi: 'वर्णित प्रजातियों की संख्या ≈ **1.7-1.8 मिलियन**।',
            },
            {
              english: '**Nomenclature** — standardised naming of organisms; agreed codes:',
        hindi: '**नामकरण**—जीवों का मानकीकृत नामकरण; सहमत कोड:',
            },
            {
              english: '• **ICBN** — International Code for Botanical Nomenclature.',
        hindi: '• **आईसीबीएन** - वानस्पतिक नामकरण के लिए अंतर्राष्ट्रीय कोड।',
            },
            {
              english: '• **ICZN** — International Code of Zoological Nomenclature.',
        hindi: '• **ICZN** - प्राणीशास्त्रीय नामकरण का अंतर्राष्ट्रीय कोड।',
            },
            {
              english: '**Binomial nomenclature** (Carolus Linnaeus): each name has 2 parts — Generic name + Specific epithet (e.g. *Mangifera indica*).',
        hindi: '**द्विपद नामकरण** (कैरोलस लिनिअस): प्रत्येक नाम के 2 भाग होते हैं - सामान्य नाम + विशिष्ट विशेषण (जैसे *मैंगीफेरा इंडिका*)।',
            },
            {
              english: 'Rules: Latin (italicised in print or **underlined separately when handwritten**); generic name capitalised, specific in lower case; author abbreviation after specific epithet (e.g. *Mangifera indica* Linn.).',
        hindi: 'नियम: लैटिन (प्रिंट में इटैलिकाइज़्ड या **हस्तलिखित होने पर अलग से रेखांकित**); सामान्य नाम बड़े अक्षरों में, छोटे अक्षरों में विशिष्ट; विशिष्ट विशेषण के बाद लेखक का संक्षिप्त नाम (उदाहरण के लिए *मंगिफेरा इंडिका* लिनन)।',
            },
            {
              english: '**Identification → Classification → Nomenclature** — three basic processes of taxonomy.',
        hindi: '**पहचान → वर्गीकरण → नामकरण** - वर्गीकरण की तीन बुनियादी प्रक्रियाएँ।',
            },
            {
              english: '**Systematics** — taxonomy + evolutionary relationships.',
        hindi: '**सिस्टमैटिक्स** - वर्गीकरण + विकासवादी संबंध।',
            },
          ],
        },
      ],
    },
    {
      id: 'taxonomic-categories',
      title: {
        english: 'Taxonomic Categories',
        hindi: 'वर्गिकीय श्रेणियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Taxonomic hierarchy** (Linnaeus) — 7 obligate categories.',
        hindi: '**टैक्सोनोमिक पदानुक्रम** (लिनिअस) - 7 बाध्यकारी श्रेणियां।',
            },
            {
              english: 'Order from lowest → highest: **Species → Genus → Family → Order → Class → Phylum (animals) / Division (plants) → Kingdom**.',
        hindi: 'निम्नतम से क्रम → उच्चतम: **प्रजाति → जीनस → परिवार → क्रम → वर्ग → फाइलम (जानवर) / प्रभाग (पौधे) → किंगडम**।',
            },
            {
              english: 'As we go higher, number of common characters **decreases**.',
        hindi: 'जैसे-जैसे हम ऊपर जाते हैं, सामान्य वर्णों की संख्या **घटती** जाती है।',
            },
            {
              english: '**Species** = group of individuals with fundamentally similar features → can interbreed (*Panthera leo*, *P. tigris* — distinct species).',
        hindi: '**प्रजाति** = मौलिक रूप से समान विशेषताओं वाले व्यक्तियों का समूह → परस्पर प्रजनन कर सकता है (*पैंथेरा लियो*, *पी. टाइग्रिस* - विशिष्ट प्रजातियाँ)।',
            },
            {
              english: '**Genus** = closely related species (*Panthera* contains lion, tiger, leopard).',
        hindi: '**जीनस** = निकट संबंधी प्रजातियाँ (*पेंथेरा* में शेर, बाघ, तेंदुआ शामिल हैं)।',
            },
            {
              english: '**Family** = related genera (Felidae — Panthera + Felis).',
        hindi: '**परिवार** = संबंधित वंश (फेलिडे - पैंथेरा + फेलिस)।',
            },
            {
              english: '**Order** = assembly of families (Carnivora — Felidae + Canidae).',
        hindi: '**आदेश** = परिवारों की सभा (कार्निवोरा - फेलिडे + कैनिडे)।',
            },
            {
              english: '**Class** = related orders (Mammalia — Carnivora + Primata).',
        hindi: '**वर्ग** = संबंधित आदेश (स्तनधारी - कार्निवोरा + प्राइमाटा)।',
            },
            {
              english: '**Phylum** = related classes (Chordata — Mammalia + Aves + Reptilia + Pisces).',
        hindi: '**फ़ाइलम** = संबंधित वर्ग (कॉर्डेटा - स्तनधारी + एवेस + सरीसृप + मीन)।',
            },
            {
              english: '**Kingdom** = highest (Animalia).',
        hindi: '**साम्राज्य** = उच्चतम (पशु)।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Common name', 'Species', 'Genus', 'Family', 'Order', 'Class'],
          rows: [
            ['Man', 'sapiens', 'Homo', 'Hominidae', 'Primata', 'Mammalia'],
            ['Housefly', 'domestica', 'Musca', 'Muscidae', 'Diptera', 'Insecta'],
            ['Mango', 'indica', 'Mangifera', 'Anacardiaceae', 'Sapindales', 'Dicotyledonae'],
            ['Wheat', 'aestivum', 'Triticum', 'Poaceae', 'Poales', 'Monocotyledonae'],
          ],
        },
      ],
    },
    {
      id: 'taxonomical-aids',
      title: {
        english: 'Taxonomical Aids',
        hindi: 'वर्गिकीय साधन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Herbarium** — store house of collected plant specimens that are dried, pressed & preserved on sheets, arranged according to a universally accepted system of classification.',
        hindi: '**हर्बेरियम** - एकत्र किए गए पौधों के नमूनों का भंडार गृह, जिन्हें वर्गीकरण की सार्वभौमिक रूप से स्वीकृत प्रणाली के अनुसार सुखाया, दबाया और चादरों पर संरक्षित किया जाता है।',
            },
            {
              english: '**Botanical Gardens** — collection of living plants for reference; each plant labelled with botanical/scientific name + family. Famous: **Royal Botanical Garden, Kew (England)**; **Indian Botanical Garden, Howrah**; **National Botanical Research Institute, Lucknow**.',
        hindi: '**वानस्पतिक उद्यान** — संदर्भ के लिए जीवित पौधों का संग्रह; प्रत्येक पौधे को botanical/scientific नाम + परिवार के साथ लेबल किया गया है। प्रसिद्ध: **रॉयल बॉटनिकल गार्डन, केव (इंग्लैंड)**; **भारतीय वनस्पति उद्यान, हावड़ा**; **राष्ट्रीय वनस्पति अनुसंधान संस्थान, लखनऊ**।',
            },
            {
              english: '**Museum** — collection of preserved plant & animal specimens for study & reference. Specimens preserved in **formalin** or as **dry specimens**; also have skeletons + skins of larger animals.',
        hindi: '**संग्रहालय** - अध्ययन और संदर्भ के लिए संरक्षित पौधों और जानवरों के नमूनों का संग्रह। **फॉर्मेलिन** या **सूखे नमूनों** के रूप में संरक्षित नमूने; बड़े जानवरों के कंकाल + खाल भी हैं।',
            },
            {
              english: '**Zoological Parks** — wild animals kept in protected environments under human care that enable us to learn about food habits & behaviour.',
        hindi: '**प्राणी उद्यान** - जंगली जानवरों को मानव देखभाल के तहत संरक्षित वातावरण में रखा जाता है जो हमें भोजन की आदतों और व्यवहार के बारे में जानने में सक्षम बनाते हैं।',
            },
            {
              english: '**Key** — taxonomical aid based on contrasting characters generally in pairs (= **couplet**); each statement = **lead**; only one lead is accepted, the other rejected; separate keys for each taxonomic category. Other records = flora, manuals, monographs, catalogues.',
        hindi: '**कुंजी** - आम तौर पर जोड़े में विपरीत वर्णों पर आधारित वर्गीकरण सहायता (= **दोहे**); प्रत्येक statement = **lead**; केवल एक लीड को स्वीकार किया जाता है, दूसरे को अस्वीकार कर दिया जाता है; प्रत्येक वर्गीकरण श्रेणी के लिए अलग-अलग कुंजियाँ। अन्य records = flora, मैनुअल, मोनोग्राफ, कैटलॉग।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Metabolism = defining feature of living organisms}',
      desc: {
        english: '',
        hindi: 'चयापचय = जीवित जीवों की परिभाषित विशेषता',
      },
    },
    {
      latex: '\\text{Hierarchy: Species} \\to \\text{Genus} \\to \\text{Family} \\to \\text{Order} \\to \\text{Class} \\to \\text{Phylum} \\to \\text{Kingdom}',
      desc: {
        english: '',
        hindi: 'पदानुक्रम: प्रजाति जीनस परिवार क्रम वर्ग फ़ाइलम किंगडम',
      },
    },
    {
      latex: '\\text{Binomial nomenclature: Genus + species (italicised)}',
      desc: {
        english: '',
        hindi: 'द्विपद नामकरण: जीनस + प्रजाति (इटैलिकाइज़्ड)',
      },
    },
    {
      latex: '\\text{ICBN (botanical) + ICZN (zoological)}',
      desc: {
        english: '',
        hindi: 'आईसीबीएन (वानस्पतिक) + आईसीजेडएन (प्राणीशास्त्र)',
      },
    },
    {
      latex: '\\text{Higher rank} \\Rightarrow \\text{fewer common characters}',
      desc: {
        english: '',
        hindi: 'उच्च रैंक कम सामान्य पात्र',
      },
    },
    {
      latex: '\\text{Mango: Mangifera indica L. (Anacardiaceae)}',
      desc: {
        english: '',
        hindi: 'आम: मैंगीफेरा इंडिका एल. (एनाकार्डियासी)',
      },
    },
    {
      latex: '\\text{Tiger: Panthera tigris (Felidae, Carnivora, Mammalia)}',
      desc: {
        english: '',
        hindi: 'बाघ: पैंथेरा टाइग्रिस (फेलिडे, कार्निवोरा, मैमलिया)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Defining',
      problem: {
        english: 'Defining feature of all living organisms is:',
        hindi: 'सभी जीवित जीवों की परिभाषित विशेषता है:',
      },
      answer: {
        english: 'Metabolism',
        hindi: 'चयापचय',
      },
      solution: {
        english: 'No non-living object shows metabolism.',
        hindi: 'कोई भी निर्जीव वस्तु चयापचय नहीं दर्शाती है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Hierarchy',
      problem: {
        english: 'Correct hierarchy from lowest to highest:',
        hindi: 'निम्नतम से उच्चतम तक सही पदानुक्रम: प्रजाति → जीनस → परिवार → गण → वर्ग → फाइलम → किंगडम',
      },
      answer: {
        english: 'Species → Genus → Family → Order → Class → Phylum → Kingdom',
        hindi: 'प्रजातियाँ → जीनस → परिवार → गण → वर्ग → फ़ाइलम → जगत',
      },
      solution: {
        english: 'Linnaean obligate categories.',
        hindi: 'लिनिअन बाध्य श्रेणियां।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Nomenclature',
      problem: {
        english: 'Author of binomial nomenclature is:',
        hindi: 'द्विपद नामकरण के लेखक हैं:',
      },
      answer: {
        english: 'Carolus Linnaeus',
        hindi: 'कैरोलस लिनिअस',
      },
      solution: {
        english: 'Systema Naturae (1758).',
        hindi: 'सिस्टेमा नेचुरे (1758)।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Family',
      problem: {
        english: 'Family of mango (Mangifera indica) is:',
        hindi: 'आम (मैंगीफेरा इंडिका) का परिवार है:',
      },
      answer: {
        english: 'Anacardiaceae',
        hindi: 'एनाकार्डियासी',
      },
      solution: {
        english: '',
        hindi: 'आम (*Mangifera indica*) का कुल **Anacardiaceae** है, इसलिए सही विकल्प Anacardiaceae है।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Aid',
      problem: {
        english: 'Dried, pressed & preserved plant specimens stored on sheets are kept in:',
        hindi: 'शीटों पर संग्रहीत सूखे, दबाए गए और संरक्षित पौधों के नमूनों को इसमें रखा जाता है:',
      },
      answer: {
        english: 'Herbarium',
        hindi: 'हर्बेरियम',
      },
      solution: {
        english: 'Arranged according to universal classification.',
        hindi: 'सार्वभौमिक वर्गीकरण के अनुसार व्यवस्थित। **अमीबॉइड** - स्यूडोपोडिया (साइटोस्केलेटन रीमॉडेल) द्वारा। जैसे अमीबा, मैक्रोफेज, ल्यूकोसाइट्स। **सिलिअरी** - सिलिया द्वारा। आंतरिक श्वासनली (बलगम और धूल), फैलोपियन ट्यूब (अंडाणु), पैरामीशियम।',
      },
    },
  ],
  next: {
    slug: 'plant-kingdom',
    title: 'Plant Kingdom',
  },
};

export default chapter;
